import { useEffect, useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { cn } from "@/lib/utils";
import { BOOKING_URL, LOGO, NAV_LINKS, PHONE_DISPLAY, PHONE_HREF } from "./data";
import { Picture } from "./Picture";

export function Header({ solid = false }: { solid?: boolean }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const inverted = !solid && !scrolled && !open;

  useEffect(() => {
    const sentinel = document.getElementById("top");
    if (!sentinel) {
      setScrolled(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => setScrolled(!entry?.isIntersecting),
      { rootMargin: "-24px 0px 0px 0px", threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  const sectionHref = (href: string) => (solid && href.startsWith("#") ? `/${href}` : href);

  return (
    <header
      className={cn(
        "fixed inset-x-0 top-0 z-50 transition-all duration-500",
        inverted ? "bg-transparent" : "bg-background/95 border-b border-border backdrop-blur-md",
      )}
    >
      <div className="mx-auto grid max-w-7xl grid-cols-[minmax(0,1fr)_auto] items-center gap-4 px-5 py-4 lg:px-8 xl:flex xl:justify-between">
        <a href={solid ? "/" : "#top"} className="flex min-w-0 items-center gap-3">
          <Picture
            image={LOGO}
            alt="The Glam Room logo"
            sizes="56px"
            imgClassName={cn(
              "h-11 w-auto shrink-0 object-contain transition-all duration-500 sm:h-14",
              inverted ? "brightness-0 invert" : "",
            )}
          />
          <span
            className={cn(
              "min-w-0 truncate font-display text-base leading-none tracking-[0.18em] uppercase transition-colors sm:text-lg xl:hidden",
              inverted ? "text-cream" : "text-foreground",
            )}
          >
            The Glam Room
          </span>
        </a>

        <nav aria-label="Main" className="hidden items-center gap-7 xl:flex">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={sectionHref(link.href)}
              {...(link.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
              className={cn(
                "text-[0.78rem] tracking-[0.14em] uppercase transition-colors hover:text-gold-deep",
                inverted ? "text-cream/90" : "text-muted-foreground",
              )}
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="flex shrink-0 items-center gap-3">
          <a
            href={PHONE_HREF}
            className={cn(
              "hidden items-center gap-2 text-sm tracking-wide transition-colors hover:text-gold-deep lg:flex",
              inverted ? "text-cream" : "text-foreground",
            )}
          >
            <Phone className="h-4 w-4" aria-hidden="true" />
            {PHONE_DISPLAY}
          </a>
          <a
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden rounded-full bg-charcoal px-5 py-2.5 text-[0.75rem] font-medium tracking-[0.16em] text-cream uppercase shadow-sm transition-all hover:-translate-y-0.5 hover:bg-charcoal/90 hover:shadow-lg sm:inline-block"
          >
            Book Appointment
          </a>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            aria-controls="mobile-navigation"
            aria-haspopup="true"
            className={cn(
              "grid h-11 w-11 place-items-center rounded-full border border-current/20 transition-colors xl:hidden",
              inverted ? "text-cream" : "text-foreground",
            )}
          >
            {open ? (
              <X className="h-5 w-5" aria-hidden="true" />
            ) : (
              <Menu className="h-5 w-5" aria-hidden="true" />
            )}
          </button>
        </div>
      </div>

      {open && (
        <nav
          id="mobile-navigation"
          aria-label="Mobile"
          className="border-t border-border bg-background px-5 pt-4 pb-8 xl:hidden"
        >
          <ul className="flex flex-col">
            {NAV_LINKS.map((link) => (
              <li key={link.href}>
                <a
                  href={sectionHref(link.href)}
                  onClick={() => setOpen(false)}
                  {...(link.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  className="block border-b border-border/60 py-3.5 font-display text-2xl text-foreground"
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
          <a
            href={PHONE_HREF}
            className="mt-6 flex items-center gap-2 text-sm tracking-[0.12em] text-muted-foreground uppercase"
          >
            <Phone className="h-4 w-4" aria-hidden="true" />
            {PHONE_DISPLAY}
          </a>
        </nav>
      )}
    </header>
  );
}
